/**
 * 离线队列服务
 * 在网络不可用时缓存操作，恢复后自动重试
 */

import fs from 'fs-extra';
import path from 'path';
import { EventEmitter } from 'events';
import { logger } from '../utils/logger';

export interface QueuedOperation {
  id: string;
  type: 'webhook' | 'api' | 'sync';
  operation: string;
  data: any;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  nextRetryTime: number;
}

export class OfflineQueue extends EventEmitter {
  private static instance: OfflineQueue;
  private queuePath: string;
  private queue: QueuedOperation[] = [];
  private isProcessing: boolean = false;
  private retryTimer: NodeJS.Timeout | null = null;
  private isOnline: boolean = true;

  // 重试策略配置
  private readonly retryConfig = {
    maxRetries: 5,
    baseDelay: 1000,    // 基础延迟 1 秒
    maxDelay: 60000,    // 最大延迟 60 秒
    backoffFactor: 2,   // 指数退避因子
    maxQueueSize: 100   // 最大队列大小
  };

  private constructor(projectRoot: string = process.cwd()) {
    super();
    this.queuePath = path.join(projectRoot, '.workflow/offline-queue.json');
    this.loadQueue();
    this.startNetworkMonitoring();
  }

  static getInstance(projectRoot?: string): OfflineQueue {
    if (!OfflineQueue.instance) {
      OfflineQueue.instance = new OfflineQueue(projectRoot);
    }
    return OfflineQueue.instance;
  }

  /**
   * 加载队列
   */
  private async loadQueue(): Promise<void> {
    try {
      if (await fs.pathExists(this.queuePath)) {
        this.queue = await fs.readJson(this.queuePath);
        logger.debug(`Loaded ${this.queue.length} queued operations`);
        
        // 启动时尝试处理队列
        if (this.queue.length > 0) {
          this.scheduleRetry();
        }
      }
    } catch (error) {
      logger.error('Failed to load offline queue:', error);
      this.queue = [];
    }
  }

  /**
   * 保存队列
   */
  private async saveQueue(): Promise<void> {
    try {
      await fs.ensureDir(path.dirname(this.queuePath));
      await fs.writeJson(this.queuePath, this.queue, { spaces: 2 });
    } catch (error) {
      logger.error('Failed to save offline queue:', error);
    }
  }

  /**
   * 添加操作到队列
   */
  async enqueue(type: QueuedOperation['type'], operation: string, data: any): Promise<void> {
    // 检查队列大小限制
    if (this.queue.length >= this.retryConfig.maxQueueSize) {
      // 移除最旧的失败操作
      const oldestFailedIndex = this.queue.findIndex(op => op.retryCount >= this.retryConfig.maxRetries);
      if (oldestFailedIndex !== -1) {
        this.queue.splice(oldestFailedIndex, 1);
      } else {
        // 如果没有失败的操作，移除最旧的
        this.queue.shift();
      }
      logger.warn(`Queue size limit reached (${this.retryConfig.maxQueueSize}), removed oldest operation`);
    }
    
    // 避免重复的操作
    const existingOp = this.queue.find(op => 
      op.type === type && 
      op.operation === operation && 
      JSON.stringify(op.data) === JSON.stringify(data)
    );
    
    if (existingOp) {
      logger.debug(`Operation already queued: ${type} ${operation}`);
      return;
    }
    
    const queuedOp: QueuedOperation = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      operation,
      data,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: this.retryConfig.maxRetries,
      nextRetryTime: Date.now() + this.retryConfig.baseDelay
    };

    this.queue.push(queuedOp);
    await this.saveQueue();

    logger.info(`Queued ${type} operation: ${operation}`);
    this.emit('operation-queued', queuedOp);

    // 如果在线，立即尝试处理
    if (this.isOnline && !this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * 处理队列
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0 || !this.isOnline) {
      return;
    }

    this.isProcessing = true;
    const now = Date.now();

    // 获取可以重试的操作
    const readyOps = this.queue.filter(op => op.nextRetryTime <= now);

    for (const op of readyOps) {
      try {
        await this.processOperation(op);
        
        // 成功，从队列中移除
        this.queue = this.queue.filter(q => q.id !== op.id);
        await this.saveQueue();
        
        logger.info(`Successfully processed queued operation: ${op.operation}`);
        this.emit('operation-succeeded', op);
      } catch (error) {
        // 失败，更新重试信息
        op.retryCount++;
        
        if (op.retryCount >= op.maxRetries) {
          // 达到最大重试次数，移除
          this.queue = this.queue.filter(q => q.id !== op.id);
          logger.error(`Operation failed after ${op.maxRetries} retries: ${op.operation}`);
          this.emit('operation-failed', op, error);
        } else {
          // 计算下次重试时间（指数退避）
          const delay = Math.min(
            this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffFactor, op.retryCount),
            this.retryConfig.maxDelay
          );
          op.nextRetryTime = Date.now() + delay;
          
          logger.debug(`Operation retry scheduled in ${delay}ms: ${op.operation}`);
        }
        
        await this.saveQueue();
      }
    }

    this.isProcessing = false;

    // 如果还有待处理的操作，安排下次重试
    if (this.queue.length > 0) {
      this.scheduleRetry();
    }
  }

  /**
   * 处理单个操作
   */
  private async processOperation(op: QueuedOperation): Promise<void> {
    // 这里应该调用实际的服务来处理操作
    // 现在只是模拟
    switch (op.type) {
      case 'webhook':
        // 重新发送 webhook
        const { webhookService } = await import('./webhook-service');
        await webhookService['sendWebhook'](op.data);
        break;
        
      case 'api':
        // 重新调用 API
        throw new Error('API retry not implemented');
        
      case 'sync':
        // 重新同步
        throw new Error('Sync retry not implemented');
        
      default:
        throw new Error(`Unknown operation type: ${op.type}`);
    }
  }

  /**
   * 安排重试
   */
  private scheduleRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }

    // 找到最早的重试时间
    const nextRetryTime = Math.min(...this.queue.map(op => op.nextRetryTime));
    const delay = Math.max(0, nextRetryTime - Date.now());

    this.retryTimer = setTimeout(() => {
      this.processQueue();
    }, delay);
  }

  /**
   * 网络状态监控
   */
  private startNetworkMonitoring(): void {
    // 简单的网络检测（可以根据需要增强）
    setInterval(async () => {
      const wasOnline = this.isOnline;
      
      // 检查是否可以访问本地 API
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch('http://localhost:3001/health', {
          method: 'GET',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        this.isOnline = response.ok;
      } catch {
        this.isOnline = false;
      }

      // 状态变化时触发事件
      if (wasOnline !== this.isOnline) {
        this.emit('network-status-changed', this.isOnline);
        
        if (this.isOnline) {
          logger.info('Network restored, processing offline queue');
          this.processQueue();
        } else {
          logger.warn('Network offline, operations will be queued');
        }
      }
    }, 10000); // 每 10 秒检查一次
  }

  /**
   * 获取队列状态
   */
  getStatus(): {
    isOnline: boolean;
    queueLength: number;
    oldestOperation: number | null;
    nextRetryIn: number | null;
  } {
    const now = Date.now();
    const oldestOp = this.queue.length > 0 
      ? Math.min(...this.queue.map(op => op.timestamp))
      : null;
    const nextRetry = this.queue.length > 0
      ? Math.min(...this.queue.map(op => op.nextRetryTime)) - now
      : null;

    return {
      isOnline: this.isOnline,
      queueLength: this.queue.length,
      oldestOperation: oldestOp ? now - oldestOp : null,
      nextRetryIn: nextRetry && nextRetry > 0 ? nextRetry : null
    };
  }

  /**
   * 清空队列
   */
  async clearQueue(): Promise<void> {
    this.queue = [];
    await this.saveQueue();
    
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    
    logger.info('Offline queue cleared');
    this.emit('queue-cleared');
  }

  /**
   * 强制重试所有操作
   */
  async retryAll(): Promise<void> {
    // 重置所有操作的重试时间
    this.queue.forEach(op => {
      op.nextRetryTime = Date.now();
    });
    
    await this.saveQueue();
    this.processQueue();
  }

  /**
   * 设置网络状态（用于测试）
   */
  setOnlineStatus(isOnline: boolean): void {
    const wasOnline = this.isOnline;
    this.isOnline = isOnline;
    
    if (wasOnline !== isOnline) {
      this.emit('network-status-changed', isOnline);
      
      if (isOnline) {
        this.processQueue();
      }
    }
  }

  /**
   * 清理资源
   */
  cleanup(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    
    this.removeAllListeners();
  }
}

// 导出单例工厂函数
export function getOfflineQueue(projectRoot?: string): OfflineQueue {
  return OfflineQueue.getInstance(projectRoot);
}